/**
 * Centralized Environment Configuration
 *
 * This module consolidates all environment variables used across the application
 * with proper validation, type safety, and secure defaults.
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface SupabaseConfig {
  url: string;
  anonKey: string;
  serviceRoleKey?: string; // Server-side only
}

interface StripeConfig {
  publishableKey: string;
  secretKey?: string; // Server-side only
  webhookSecret?: string; // Server-side only
}

interface AIConfig {
  geminiApiKey?: string;
  openRouterApiKey?: string;
}

interface AppConfig {
  url: string;
  env: 'development' | 'test' | 'staging' | 'production';
  isProduction: boolean;
  isDevelopment: boolean;
  isTest: boolean;
}

interface AssetConfig {
  socialImage: string;
  heroDragDropImage: string;
  heroVSCodeFeatureImage: string;
  extensionPanelImage: string;
  extensionFeatureImage: string;
}

interface EnvironmentConfig {
  supabase: SupabaseConfig;
  stripe: StripeConfig;
  ai: AIConfig;
  app: AppConfig;
  assets: AssetConfig;
  analytics?: {
    gaTrackingId?: string;
  };
  logging?: {
    level: 'error' | 'warn' | 'info' | 'debug';
  };
  features?: {
    demoMode: boolean;
    testMode: boolean;
    metricsDemo: boolean;
  };
}

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validates that a required environment variable is present and not empty
 */
function validateRequiredEnvVar(name: string, value?: string): string {
  if (!value || value.trim() === '') {
    throw new Error(`Missing required environment variable: ${name}`);
  }

  // Check for placeholder values
  const placeholderPatterns = [
    /your_.*_here/i,
    /replace_me/i,
    /placeholder/i,
    /example/i,
    /test.*key/i,
  ];

  for (const pattern of placeholderPatterns) {
    if (pattern.test(value)) {
      throw new Error(`Environment variable ${name} appears to contain a placeholder value`);
    }
  }

  return value.trim();
}

/**
 * Validates URL format
 */
function validateUrl(name: string, value: string): string {
  const validUrl = validateRequiredEnvVar(name, value);

  try {
    new URL(validUrl);
    return validUrl;
  } catch {
    throw new Error(`Invalid URL format for ${name}: ${validUrl}`);
  }
}

/**
 * Validates Supabase URL format
 */
function validateSupabaseUrl(value: string): string {
  const url = validateUrl('VITE_SUPABASE_URL', value);

  // Supabase URLs should follow the pattern: https://[project-id].supabase.co
  if (!url.includes('.supabase.co') && !url.includes('localhost')) {
    console.warn(`Warning: VITE_SUPABASE_URL does not appear to be a standard Supabase URL: ${url}`);
  }

  return url;
}

/**
 * Validates Stripe publishable key format
 */
function validateStripePublishableKey(value: string): string {
  const key = validateRequiredEnvVar('VITE_STRIPE_PUBLISHABLE_KEY', value);

  // Stripe publishable keys start with 'pk_test_' or 'pk_live_'
  if (!key.startsWith('pk_test_') && !key.startsWith('pk_live_')) {
    throw new Error('Invalid Stripe publishable key format. Must start with pk_test_ or pk_live_');
  }

  return key;
}

/**
 * Validates log level
 */
function validateLogLevel(value?: string): 'error' | 'warn' | 'info' | 'debug' {
  const validLevels = ['error', 'warn', 'info', 'debug'] as const;
  const level = (value || 'info').toLowerCase() as typeof validLevels[number];

  if (!validLevels.includes(level)) {
    console.warn(`Invalid log level: ${value}. Defaulting to 'info'`);
    return 'info';
  }

  return level;
}

// ============================================================================
// ENVIRONMENT VARIABLE READERS
// ============================================================================

/**
 * Reads and validates Supabase configuration
 */
function getSupabaseConfig(): SupabaseConfig {
  return {
    url: validateSupabaseUrl(import.meta.env.VITE_SUPABASE_URL),
    anonKey: validateRequiredEnvVar('VITE_SUPABASE_ANON_KEY', import.meta.env.VITE_SUPABASE_ANON_KEY),
    serviceRoleKey: import.meta.env.SUPABASE_SERVICE_ROLE_KEY, // Server-side only
  };
}

/**
 * Reads and validates Stripe configuration
 */
function getStripeConfig(): StripeConfig {
  return {
    publishableKey: validateStripePublishableKey(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''),
    secretKey: import.meta.env.STRIPE_SECRET_KEY, // Server-side only
    webhookSecret: import.meta.env.STRIPE_WEBHOOK_SECRET, // Server-side only
  };
}

/**
 * Reads and validates AI services configuration
 */
function getAIConfig(): AIConfig {
  return {
    geminiApiKey: import.meta.env.GEMINI_API_KEY,
    openRouterApiKey: import.meta.env.VITE_OPENROUTER_API_KEY,
  };
}

/**
 * Reads and validates application configuration
 */
function getAppConfig(): AppConfig {
  const env = (import.meta.env.VITE_NODE_ENV ||
               import.meta.env.NODE_ENV ||
               'development') as EnvironmentConfig['app']['env'];

  const validEnvs = ['development', 'test', 'staging', 'production'] as const;
  if (!validEnvs.includes(env as any)) {
    console.warn(`Invalid environment: ${env}. Defaulting to 'development'`);
  }

  const normalizedEnv = validEnvs.includes(env as any) ? env : 'development';

  return {
    url: import.meta.env.VITE_APP_URL || 'http://localhost:3000',
    env: normalizedEnv,
    isProduction: normalizedEnv === 'production',
    isDevelopment: normalizedEnv === 'development',
    isTest: normalizedEnv === 'test',
  };
}

/**
 * Reads and validates asset configuration
 */
function getAssetConfig(): AssetConfig {
  return {
    socialImage: import.meta.env.VITE_SOCIAL_IMAGE || '/screenshots/vscode-extension-placeholder.svg',
    heroDragDropImage: import.meta.env.VITE_HERO_DRAG_DROP_IMAGE || '/screenshots/drag-drop-placeholder.svg',
    heroVSCodeFeatureImage: import.meta.env.VITE_HERO_VSCODE_FEATURE_IMAGE || '/screenshots/vscode-extension-feature-placeholder.svg',
    extensionPanelImage: import.meta.env.VITE_EXTENSION_PANEL_IMAGE || '/screenshots/vscode-extension-placeholder.svg',
    extensionFeatureImage: import.meta.env.VITE_EXTENSION_FEATURE_IMAGE || '/screenshots/vscode-extension-feature-placeholder.svg',
  };
}

/**
 * Reads and validates analytics configuration
 */
function getAnalyticsConfig() {
  return {
    gaTrackingId: import.meta.env.VITE_GA_TRACKING_ID,
  };
}

/**
 * Reads and validates logging configuration
 */
function getLoggingConfig() {
  return {
    level: validateLogLevel(import.meta.env.VITE_LOG_LEVEL),
  };
}

/**
 * Reads and validates feature flags
 */
function getFeatureConfig() {
  return {
    demoMode: import.meta.env.VITE_DEMO_MODE === 'true',
    testMode: import.meta.env.VITE_TEST_MODE === 'true',
    metricsDemo: import.meta.env.VITE_METRICS_DEMO === '1',
  };
}

// ============================================================================
// MAIN CONFIGURATION OBJECT
// ============================================================================

/**
 * Centralized environment configuration
 *
 * This object consolidates all environment variables with validation and type safety.
 * It provides a single source of truth for configuration across the application.
 */
const config: EnvironmentConfig = {
  supabase: getSupabaseConfig(),
  stripe: getStripeConfig(),
  ai: getAIConfig(),
  app: getAppConfig(),
  assets: getAssetConfig(),
  analytics: getAnalyticsConfig(),
  logging: getLoggingConfig(),
  features: getFeatureConfig(),
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Checks if Supabase is properly configured
 */
export function isSupabaseConfigured(): boolean {
  try {
    return !!(config.supabase.url && config.supabase.anonKey);
  } catch {
    return false;
  }
}

/**
 * Checks if Stripe is properly configured
 */
export function isStripeConfigured(): boolean {
  try {
    return !!(config.stripe.publishableKey && config.stripe.publishableKey.startsWith('pk_'));
  } catch {
    return false;
  }
}

/**
 * Checks if AI services are configured
 */
export function isAIConfigured(): boolean {
  return !!(config.ai.geminiApiKey || config.ai.openRouterApiKey);
}

/**
 * Gets the current environment
 */
export function getEnvironment(): EnvironmentConfig['app']['env'] {
  return config.app.env;
}

/**
 * Checks if running in production
 */
export function isProduction(): boolean {
  return config.app.isProduction;
}

/**
 * Checks if running in development
 */
export function isDevelopment(): boolean {
  return config.app.isDevelopment;
}

/**
 * Checks if running in test mode
 */
export function isTest(): boolean {
  return config.app.isTest;
}

/**
 * Validates the entire environment configuration
 */
export function validateEnvironment(): void {
  // Check for security issues
  const serverOnlyVars = ['STRIPE_SECRET_KEY', 'STRIPE_WEBHOOK_SECRET', 'SUPABASE_SERVICE_ROLE_KEY'];
  const exposedServerVars = serverOnlyVars.filter(varName =>
    varName.startsWith('VITE_') && import.meta.env[varName]
  );

  if (exposedServerVars.length > 0) {
    throw new Error(`Security Error: Server-side secrets must not be exposed to client-side code: ${exposedServerVars.join(', ')}`);
  }

  // Validate critical configurations
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase configuration is incomplete. Please check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
  }

  // Warn about missing optional configurations
  if (!isStripeConfigured()) {
    console.warn('Stripe configuration is incomplete. Subscription features will be disabled.');
  }

  if (!isAIConfigured()) {
    console.info('AI services are not configured. AI features will be unavailable.');
  }
}

// ============================================================================
// CONFIGURATION EXPORT
// ============================================================================

/**
 * The main configuration object
 *
 * Usage:
 * ```typescript
 * import { config } from '@/lib/env';
 *
 * // Access Supabase config
 * const supabaseUrl = config.supabase.url;
 *
 * // Check if features are enabled
 * if (config.features.demoMode) {
 *   // Show demo content
 * }
 * ```
 */
export { config };

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type { EnvironmentConfig, SupabaseConfig, StripeConfig, AIConfig, AppConfig, AssetConfig };

// ============================================================================
// INITIALIZATION
// ============================================================================

// Validate configuration on module load (development only)
if (isDevelopment()) {
  try {
    validateEnvironment();
    console.info('✅ Environment configuration validated successfully');
  } catch (error) {
    console.error('❌ Environment configuration validation failed:', error);
    // Don't throw in development to allow for easier debugging
  }
}